import { useState } from "react";
import { FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import styles from "./styles.module.css";
import EmailManager from "../../database/EmailManager";
import { Button } from "../../components/UI";
type Props = {
  primaryEmail: string;
  alternativeEmails: string[];
  refreshProfile: () => void;
  onClose?: () => void;
};
export default function ManageEmails({
  primaryEmail,
  alternativeEmails,
  refreshProfile,
  onClose,
}: Props) {
  const [newEmail, setNewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const handleAddEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    const email = newEmail.trim();
    if (!email || alternativeEmails.includes(email) || email === primaryEmail) {
      toast.info("This email is already on your account.");
      return;
    }
    setIsLoading(true);
    try {
      const added = await EmailManager.addAlternativeEmail(primaryEmail, email);
      refreshProfile();
      if (added) {
        toast.success("Email added successfully");
        setNewEmail("");
      } else toast.error("Failed to add email");
    } catch (error) {
      toast.error("Failed to add email");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleRemoveEmail = async (email: string) => {
    if (
      isLoading ||
      !window.confirm(`Are you sure you want to remove ${email}?`)
    )
      return;
    setIsLoading(true);
    try {
      const removed = await EmailManager.removeAlternativeEmail(
        primaryEmail,
        email,
      );
      refreshProfile();
      if (removed) toast.success("Email removed successfully");
      else toast.error("Failed to remove email");
    } catch (error) {
      toast.error("Failed to remove email");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSwapEmail = async (email: string) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const swapped = await EmailManager.swapPrimaryEmail(primaryEmail, email);
      refreshProfile();
      if (swapped === false) toast.error("Failed to change primary email");
    } catch (error) {
      toast.error("Failed to change primary email");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className={styles.container} aria-busy={isLoading}>
      <div className={styles.email}>
        <span>Primary email</span>
        <p>{primaryEmail}</p>
      </div>
      <hr />
      <div className={styles.alternatives}>
        {alternativeEmails.length ? (
          alternativeEmails.map((email) => (
            <div key={email} className={styles.alternative}>
              <div className={styles.email}>
                <span>Alternative email</span>
                <p>{email}</p>
              </div>
              <div className={styles.actions}>
                <Button
                  onClick={() => handleSwapEmail(email)}
                  disabled={isLoading}
                >
                  Set as primary
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleRemoveEmail(email)}
                  disabled={isLoading}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))
        ) : (
          <p className={styles.note}>No alternative emails.</p>
        )}
      </div>
      <hr />
      <form onSubmit={handleAddEmail} className={styles.form}>
        <label htmlFor="new-email">Add an email address</label>
        <input
          id="new-email"
          type="email"
          autoComplete="email"
          placeholder="name@school.org"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          required
          disabled={isLoading}
        />
        <div className={styles.footer}>
          <Button type="submit" disabled={isLoading || !newEmail.trim()}>
            <FiPlus aria-hidden="true" />
            Add email
          </Button>
          {onClose && (
            <Button variant="primary" onClick={onClose} disabled={isLoading}>
              Done
            </Button>
          )}
        </div>
      </form>
      {isLoading && (
        <p className={styles.note} role="status">
          Updating emails…
        </p>
      )}
    </div>
  );
}
